import DOMPurify from 'dompurify';

export const sanitizeInput = (input: string): string => {
  if (!input || typeof input !== 'string') return '';
  
  // Use DOMPurify for comprehensive sanitization
  const sanitized = DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: []
  });
  
  return sanitized
    .replace(/javascript:/gi, '')
    .replace(/on\w+=/gi, '')
    .replace(/data:/gi, '')
    .trim()
    .slice(0, 10000); // Limit length
};

export const validateApiKey = (apiKey: string): boolean => {
  if (!apiKey || typeof apiKey !== 'string') return false;
  if (apiKey.length < 20 || apiKey.length > 500) return false;
  
  // Check for valid API key patterns
  const validPatterns = [
    /^sk-[a-zA-Z0-9]{20,}$/, // OpenAI
    /^sk-ant-[a-zA-Z0-9_-]{20,}$/, // Anthropic
    /^[a-zA-Z0-9_-]{20,}$/, // Generic
  ];
  
  const isValidPattern = validPatterns.some(pattern => pattern.test(apiKey));
  if (!isValidPattern) return false;
  
  // Block test/dummy keys
  const blockedPatterns = [
    /test/i,
    /dummy/i,
    /fake/i,
    /example/i,
    /placeholder/i,
    /your_api_key/i,
    /insert_key/i
  ];
  
  return !blockedPatterns.some(pattern => pattern.test(apiKey));
};

export const validateUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false;
  
  try {
    const parsedUrl = new URL(url);
    
    // Only allow HTTPS in production
    if (import.meta.env.PROD && parsedUrl.protocol !== 'https:') {
      return false;
    }
    
    // Allow HTTP for development
    if (!import.meta.env.PROD && !['http:', 'https:'].includes(parsedUrl.protocol)) {
      return false;
    }
    
    // Block dangerous protocols
    const blockedProtocols = ['javascript:', 'data:', 'file:', 'ftp:', 'gopher:', 'ldap:', 'dict:', 'sftp:', 'tftp:'];
    if (blockedProtocols.includes(parsedUrl.protocol)) {
      return false;
    }
    
    // Block private IP ranges and localhost in production
    if (import.meta.env.PROD) {
      const hostname = parsedUrl.hostname;
      const privateIpRanges = [
        /^127\./,           // 127.0.0.0/8 (localhost)
        /^10\./,            // 10.0.0.0/8 (private)
        /^172\.(1[6-9]|2[0-9]|3[0-1])\./,  // 172.16.0.0/12 (private)
        /^192\.168\./,      // 192.168.0.0/16 (private)
        /^169\.254\./,      // 169.254.0.0/16 (link-local)
        /^::1$/,            // IPv6 localhost
        /^fc00:/,           // IPv6 private
        /^fe80:/            // IPv6 link-local
      ];
      
      if (hostname === 'localhost' || privateIpRanges.some(range => range.test(hostname))) {
        return false;
      }
    }
    
    return true;
  } catch {
    return false;
  }
};

export const validateRequestOrigin = (origin: string | undefined, allowedOrigins: string[]): boolean => {
  if (!origin) return false;
  return allowedOrigins.includes(origin) || allowedOrigins.includes('*');
};

export const sanitizeHeaders = (headers: Record<string, string>): Record<string, string> => {
  const sanitized: Record<string, string> = {};
  const allowedHeaders = ['content-type', 'authorization', 'user-agent', 'accept'];
  
  for (const [key, value] of Object.entries(headers)) {
    const lowerKey = key.toLowerCase();
    if (allowedHeaders.includes(lowerKey) && typeof value === 'string') {
      sanitized[key] = sanitizeInput(value);
    }
  }
  
  return sanitized;
};

export const rateLimit = {
  attempts: new Map<string, { count: number; resetTime: number }>(),
  blocked: new Set<string>(),
  
  check(identifier: string, maxAttempts: number = 10, windowMs: number = 60000): boolean {
    // Check if IP is permanently blocked
    if (this.blocked.has(identifier)) {
      return false;
    }
    
    const now = Date.now();
    const record = this.attempts.get(identifier);
    
    if (!record || now > record.resetTime) {
      this.attempts.set(identifier, { count: 1, resetTime: now + windowMs });
      return true;
    }
    
    if (record.count >= maxAttempts) {
      return false;
    }
    
    record.count++;
    return true;
  },
  
  block(identifier: string): void {
    this.blocked.add(identifier);
  },
  
  unblock(identifier: string): void {
    this.blocked.delete(identifier);
    this.attempts.delete(identifier);
  },
  
  reset(identifier: string): void {
    this.attempts.delete(identifier);
  },
  
  cleanup(): void {
    const now = Date.now();
    for (const [key, record] of this.attempts.entries()) {
      if (now > record.resetTime) {
        this.attempts.delete(key);
      }
    }
  }
};

// Cleanup rate limit cache every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(() => rateLimit.cleanup(), 5 * 60 * 1000);
}

export const generateSecureHeaders = () => ({
  'Content-Security-Policy': "default-src 'self'; script-src 'self' 'unsafe-eval'; style-src 'self' 'unsafe-inline'; img-src 'self' data: https:; connect-src 'self' https:; font-src 'self' data:; object-src 'none'; base-uri 'self'; form-action 'self'; frame-ancestors 'none'; upgrade-insecure-requests;",
  'X-Frame-Options': 'DENY',
  'X-Content-Type-Options': 'nosniff',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Permissions-Policy': 'geolocation=(), microphone=(), camera=(), payment=(), usb=(), accelerometer=(), gyroscope=(), magnetometer=()',
  'Strict-Transport-Security': 'max-age=31536000; includeSubDomains',
  'X-XSS-Protection': '1; mode=block',
  'Cache-Control': 'no-store, no-cache, must-revalidate, proxy-revalidate',
  'Pragma': 'no-cache',
  'Expires': '0',
  'Cross-Origin-Embedder-Policy': 'require-corp',
  'Cross-Origin-Opener-Policy': 'same-origin',
  'Cross-Origin-Resource-Policy': 'same-origin'
});

export const logSecurityEvent = (event: {
  type: 'auth_attempt' | 'api_call' | 'validation_error' | 'rate_limit' | 'security_violation';
  user?: string;
  ip?: string;
  details?: any;
  success: boolean;
}) => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    level: event.success ? 'info' : 'warn',
    ...event
  };
  
  // Only log in development or if explicitly enabled
  if (!import.meta.env.PROD || import.meta.env.VITE_ENABLE_SECURITY_LOGGING === 'true') {
    console.log('[SECURITY]', logEntry);
  }
};